<template>
  <div class="cargo-insurance-certificate-detail flex flex-column gap-4">
    <!-- header -->
    <div class="flex flex-column gap-4">
      <!-- product -->
      <div class="flex justify-content-between align-items-center">
        <h3>
          {{ cargoInsuranceCertificate.productName }}
        </h3>
        <div>
          <img
            src="@/assets/img/loadSureLogo.png"
            height="30px"
            :alt="t('cargoInsurance.loadsure')"
          />
        </div>
      </div>
      <div
        v-if="cargoInsuranceCertificate.status === 'CERTIFICATE_CANCELLED'"
        class="text-danger font-bold"
      >
        {{ titleCase(cargoInsuranceCertificate.status) }}
      </div>
      <div class="text-sm">
        {{ cargoInsuranceCertificate.productDescription }}
      </div>

      <!-- certificate -->
      <div v-if="certificateNumber" class="text-sm">
        {{ t('cargoInsurance.certificate.certificateNumber') }}:
        {{ certificateNumber }}
        <br />
        <a v-if="certificateLink" :href="certificateLink" target="_blank">
          {{ t('cargoInsurance.certificate.viewCertificateLinkText') }}
        </a>
      </div>

      <!-- limits -->
      <div class="mt-2 flex flex-column gap-4">
        <div class="flex justify-content-between">
          <span>{{ t('cargoInsurance.pricing.limit') }}:</span>
          <span>{{ renderMoney(cargoInsuranceCertificate.limit) }}</span>
        </div>
        <div class="flex justify-content-between">
          <span>{{ t('cargoInsurance.pricing.deductible') }}:</span>
          <span>{{ renderMoney(cargoInsuranceCertificate.deductible) }}</span>
        </div>
      </div>
    </div>

    <!-- separator -->
    <div class="border-bottom-1 border-200" />

    <!-- pricing -->
    <div class="flex flex-column gap-4">
      <div
        v-if="cargoInsuranceCertificate.premium"
        class="flex justify-content-between"
      >
        <span>{{ t('cargoInsurance.pricing.premium') }}:</span>
        <span>{{ renderMoney(cargoInsuranceCertificate.premium) }}</span>
      </div>
      <div
        v-if="
          cargoInsuranceCertificate.serviceFee &&
          cargoInsuranceCertificate.serviceFee?.amount !== 0
        "
        class="flex justify-content-between"
      >
        <span>{{ t('cargoInsurance.pricing.serviceFee') }}:</span>
        <span>{{ renderMoney(cargoInsuranceCertificate.serviceFee) }}</span>
      </div>
      <div
        v-if="cargoInsuranceCertificate.tax"
        class="flex justify-content-between"
      >
        <span>{{ t('cargoInsurance.pricing.tax') }}:</span>
        <span>{{ renderMoney(cargoInsuranceCertificate.tax) }}</span>
      </div>
      <div
        v-if="
          cargoInsuranceCertificate.uplift &&
          cargoInsuranceCertificate.uplift?.amount !== 0
        "
        class="flex justify-content-between"
      >
        <span>{{ t('cargoInsurance.pricing.uplift') }}:</span>
        <span>{{ renderMoney(cargoInsuranceCertificate.uplift) }}</span>
      </div>
      <div class="font-bold flex justify-content-between">
        <span>{{ t('cargoInsurance.pricing.totalCost') }}:</span>
        <span>{{ renderMoney(cargoInsuranceCertificate.totalCost) }}</span>
      </div>
    </div>

    <!-- terms and conditions link -->
    <div class="text-sm">
      <span>
        {{ t('cargoInsurance.termsAndConditions.linkReference.prefix') }}
      </span>
      <span>
        <a
          :href="cargoInsuranceCertificate.termsAndConditionsLink"
          target="_blank"
        >
          {{ t('cargoInsurance.termsAndConditions.linkText') }}
        </a>
      </span>
      <span>
        {{ t('cargoInsurance.termsAndConditions.linkReference.suffix') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney, titleCase } from '@rygen/rygen-ui/i18n';

import {
  CargoInsuranceCertificate,
  CargoInsuranceQuote,
} from '@/model/cargoInsurance';

const props = defineProps<{
  cargoInsuranceCertificate: CargoInsuranceQuote | CargoInsuranceCertificate;
}>();

const { cargoInsuranceCertificate } = toRefs(props);

const { t } = useI18n();

const certificateNumber: Ref<string | undefined> = computed(() => {
  return cargoInsuranceCertificate.value instanceof CargoInsuranceCertificate
    ? cargoInsuranceCertificate.value.certificateNumber
    : undefined;
});

const certificateLink: Ref<string | undefined> = computed(() => {
  return cargoInsuranceCertificate.value instanceof CargoInsuranceCertificate
    ? cargoInsuranceCertificate.value.certificateLink
    : undefined;
});
</script>
